import { keepPreviousData, useQuery, type UseQueryOptions } from "@tanstack/react-query";

import type { ActivationContractGoogleAnalyticsGaMappingResponse } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { apiClient, getQueryKey } from "app/modules/api-client";
import { isResourceNotFoundError, ResponseError } from "app/services/fetchService";

export interface IUseActivationGaMappingDetailQueryOptions extends Omit<
    UseQueryOptions<ActivationContractGoogleAnalyticsGaMappingResponse>,
    "queryKey" | "queryFn"
> {
    shouldIgnoreResourceNotFoundError?: boolean;
}

export const useActivationGaMappingDetailQuery = (
    activationId: string,
    { shouldIgnoreResourceNotFoundError, ...options }: IUseActivationGaMappingDetailQueryOptions = {},
) =>
    useQuery({
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
        ...options,
        queryKey: getQueryKey({ tag: "Activation", methodName: "activationGaMappingDetail", args: [activationId] }),
        queryFn: async () => {
            try {
                return await apiClient.Activation.activationGaMappingDetail(activationId);
            } catch (error) {
                // Ignore RESOURCE_NOT_FOUND error
                if (
                    shouldIgnoreResourceNotFoundError &&
                    isResourceNotFoundError(error)
                ) {
                    return null;
                }

                Sentry.errorTracker({
                    e: error,
                    message: "Failed to load Activation.activationGaMappingDetail data",
                    level: "error",
                    includeCustomFingerPrint: [
                        "activation-ga-mapping-detail-load-failed",
                        error instanceof ResponseError ? String(error.status) : "unknown",
                    ],
                    params: {
                        extra: {
                            activationId,
                        },
                    },
                });

                throw error;
            }
        },
    });
